import { useState } from 'react'
import {
  Lightbulb, Check, X, ChevronLeft, ChevronRight,
  Search, Sparkles, ArrowRight, Target, FileText, MapPin,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useRecommendations, useApplyRecommendation, useDismissRecommendation } from '../hooks/useGFO'

const statusTabs = [
  { value: undefined, label: 'Toutes' },
  { value: 'pending', label: 'À traiter' },
  { value: 'applied', label: 'Appliquées' },
  { value: 'dismissed', label: 'Ignorées' },
]

const typeIcons: Record<string, any> = {
  keyword: Search,
  review: Sparkles,
  landing_page: FileText,
  gbp_field: MapPin,
}

const impactStyles: Record<string, string> = {
  high: 'bg-red-50 text-red-600 border-red-200',
  medium: 'bg-amber-50 text-amber-600 border-amber-200',
  low: 'bg-sky-50 text-sky-600 border-sky-200',
}

const impactLabels: Record<string, string> = {
  high: 'Impact élevé',
  medium: 'Impact moyen',
  low: 'Impact faible',
}

export function OpportunitiesPage() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data, isLoading } = useRecommendations(page, statusFilter)
  const recos = data?.recommendations || []
  const meta = data?.meta

  const apply = useApplyRecommendation()
  const dismiss = useDismissRecommendation()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Opportunités</h1>
        <p className="text-sm text-ink-faint mt-1">
          Recommandations IA pour optimiser votre empreinte Google
        </p>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 rounded-card bg-paper-warm p-1 w-fit">
        {statusTabs.map((tab) => (
          <button
            key={tab.label}
            onClick={() => { setStatusFilter(tab.value); setPage(1) }}
            className={cn(
              'rounded-lg px-4 py-2 text-xs font-medium transition-all',
              statusFilter === tab.value
                ? 'bg-white text-ink shadow-soft'
                : 'text-ink-muted hover:text-ink',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-card border border-ink-border bg-white p-5 animate-pulse">
              <div className="space-y-3">
                <div className="h-4 w-48 rounded bg-paper-warm" />
                <div className="h-3 w-full rounded bg-paper-warm" />
              </div>
            </div>
          ))
        ) : recos.length === 0 ? (
          <div className="rounded-card border border-ink-border bg-white p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-paper-warm">
              <Lightbulb size={24} className="text-ink-faint" />
            </div>
            <p className="text-sm text-ink-muted">
              {statusFilter === 'pending'
                ? 'Aucune recommandation en attente'
                : 'Aucune recommandation trouvée'}
            </p>
          </div>
        ) : (
          recos.map((reco: any) => {
            const Icon = typeIcons[reco.type] || Target
            const isExpanded = expanded === reco.id

            return (
              <div
                key={reco.id}
                className={cn(
                  'rounded-card border bg-white shadow-soft transition-all animate-slide-up overflow-hidden',
                  reco.status === 'applied'
                    ? 'border-emerald-200 bg-emerald-50/30'
                    : reco.status === 'dismissed'
                    ? 'border-ink-border opacity-60'
                    : 'border-l-4 border-l-brick border-ink-border',
                )}
              >
                <div
                  className="flex items-start gap-4 p-5 cursor-pointer"
                  onClick={() => setExpanded(isExpanded ? null : reco.id)}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-gold-light text-brick">
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn(
                        'inline-flex items-center rounded-lg border px-2 py-0.5 text-[10px] font-semibold',
                        impactStyles[reco.estimated_impact] || impactStyles.medium,
                      )}>
                        {impactLabels[reco.estimated_impact] || reco.estimated_impact}
                      </span>
                      <span className="text-[10px] font-medium text-ink-faint uppercase tracking-wider">
                        Priorité {reco.priority}
                      </span>
                      {reco.status === 'applied' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                          <Check size={10} /> Appliquée
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1.5 text-sm font-semibold text-ink">{reco.title}</h3>
                    <p className="mt-1 text-xs text-ink-muted line-clamp-2">{reco.description}</p>
                  </div>
                  <ArrowRight
                    size={16}
                    className={cn(
                      'shrink-0 text-ink-faint transition-transform',
                      isExpanded && 'rotate-90',
                    )}
                  />
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-ink-border px-5 pb-5 pt-4 space-y-4 animate-fade-in">
                    {reco.action_text && (
                      <div className="rounded-lg bg-paper border border-ink-border p-4">
                        <p className="text-xs font-medium text-ink-muted mb-2">Contenu suggéré</p>
                        <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">
                          {reco.action_text}
                        </p>
                      </div>
                    )}

                    {reco.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); apply.mutate(reco.id) }}
                          disabled={apply.isPending}
                          className="inline-flex items-center gap-1.5 rounded-card bg-brick px-4 py-2 text-xs font-medium text-white hover:bg-brick transition-colors disabled:opacity-50"
                        >
                          <Check size={14} /> Appliquer
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); dismiss.mutate(reco.id) }}
                          disabled={dismiss.isPending}
                          className="inline-flex items-center gap-1.5 rounded-card border border-ink-border px-4 py-2 text-xs font-medium text-ink-muted hover:bg-paper transition-colors disabled:opacity-50"
                        >
                          <X size={14} /> Ignorer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {meta && meta.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-faint">{meta.total} recommandation{meta.total > 1 ? 's' : ''}</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 py-1 text-xs font-medium text-ink-muted">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={recos.length < 20}
              className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
